import React, { useState, useRef, useEffect } from 'react';
import { db, storage } from '../../firebase';
import { doc, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { X, Plus, Upload, ChevronDown, Film, Camera, Image } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';

const EditEpisodeModal = ({ isOpen, onClose, showId, showName, userId, episodeId, episode }) => {
  const [episodeTitle, setEpisodeTitle] = useState('');
  const [episodeDescription, setEpisodeDescription] = useState('');
  const [episodeDuration, setEpisodeDuration] = useState(22);
  const [episodeImage, setEpisodeImage] = useState(null);
  const [episodeImagePreview, setEpisodeImagePreview] = useState('');
  const [episodeScenes, setEpisodeScenes] = useState([]);
  const [episodeScenesPreview, setEpisodeScenesPreview] = useState([]);
  const [savingEpisode, setSavingEpisode] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(1);
  const [showSeasonSelector, setShowSeasonSelector] = useState(false);
  const [titleError, setTitleError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);
  
  // Set date from episode or today
  const today = new Date();
  const formattedDate = today.toISOString().split('T')[0];
  const [date, setDate] = useState(formattedDate);
  
  const fileInputRef = useRef(null);
  const scenesInputRef = useRef(null);
  
  // Load episode data
  useEffect(() => {
    if (episode) {
      setEpisodeTitle(episode.episodeTitle || '');
      setEpisodeDescription(episode.episodeDescription || '');
      setEpisodeDuration(episode.episodeDuration || 22);
      setEpisodeImagePreview(episode.episodeImage || '');
      
      if (episode.episodeScenes && episode.episodeScenes.length > 0) {
        setEpisodeScenes(episode.episodeScenes);
        setEpisodeScenesPreview(episode.episodeScenes.map(scene => scene.url || ''));
      }
      
      if (episode.episodeSeason) {
        setSelectedSeason(episode.episodeSeason);
      }
      
      if (episode.episodeDate && episode.episodeDate.toDate) {
        const episodeDate = episode.episodeDate.toDate();
        setDate(episodeDate.toISOString().split('T')[0]);
      }
    }
  }, [episode]);
  
  // Validate inputs when they change
  useEffect(() => {
    // Check title length
    if (episodeTitle.length > 26) {
      setTitleError(true);
    } else {
      setTitleError(false);
    }
    
    // Check description word count
    const wordCount = episodeDescription.trim().split(/\s+/).length;
    if (episodeDescription.trim() !== '' && wordCount > 26) {
      setDescriptionError(true);
    } else {
      setDescriptionError(false);
    }
  }, [episodeTitle, episodeDescription]);
  
  if (!isOpen) return null;
  
  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setEpisodeImage(file);
      setEpisodeImagePreview(URL.createObjectURL(file));
    }
  };
  
  const handleImageClick = () => {
    fileInputRef.current.click();
  };
  
  const toggleDuration = () => {
    setEpisodeDuration(episodeDuration === 22 ? 44 : 22);
  };
  
  const handleScenesChange = (e) => {
    if (e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setEpisodeScenes([...episodeScenes, ...files]);
      
      // Create preview URLs
      const newPreviews = files.map(file => URL.createObjectURL(file));
      setEpisodeScenesPreview([...episodeScenesPreview, ...newPreviews]);
    }
  };
  
  const handleRemoveScene = (index) => {
    const updatedScenes = [...episodeScenes];
    const updatedPreviews = [...episodeScenesPreview];
    
    // If it's a File object, we need to revoke the object URL
    if (updatedPreviews[index] && typeof updatedPreviews[index] === 'string' && updatedPreviews[index].startsWith('blob:')) {
      URL.revokeObjectURL(updatedPreviews[index]);
    }
    
    updatedScenes.splice(index, 1);
    updatedPreviews.splice(index, 1);
    
    setEpisodeScenes(updatedScenes);
    setEpisodeScenesPreview(updatedPreviews);
  };
  
  const handleSeasonChange = (season) => {
    setSelectedSeason(season);
    setShowSeasonSelector(false);
  };
  
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!episodeTitle.trim() || !episodeDescription.trim() || !episodeImagePreview || titleError || descriptionError) {
      return;
    }
    
    try {
      setSavingEpisode(true);
      setError(null);
      
      // Upload episode cover image if changed
      let episodeImageUrl = episode.episodeImage;
      if (episodeImage) {
        const imageRef = ref(storage, `shows/${showId}/episodes/${episodeId}/cover_${Date.now()}`);
        await uploadBytes(imageRef, episodeImage);
        episodeImageUrl = await getDownloadURL(imageRef);
      }
      
      // Upload scenes if any new ones
      const uploadedScenes = [];
      
      for (let i = 0; i < episodeScenes.length; i++) {
        const scene = episodeScenes[i];
        
        // If it's a File object, upload it
        if (scene instanceof File) {
          const sceneRef = ref(storage, `shows/${showId}/episodes/${episodeId}/scenes/scene_${i}_${Date.now()}`);
          await uploadBytes(sceneRef, scene);
          const sceneUrl = await getDownloadURL(sceneRef);
          uploadedScenes.push({ url: sceneUrl });
        } else {
          // It's an existing scene object, keep it as is
          uploadedScenes.push(scene);
        }
      }
      
      // Parse date
      const episodeDate = new Date(date);
      
      // Update episode document
      const episodeRef = doc(db, 'shows', showId, 'episodes', episodeId);
      
      const updatedEpisodeData = {
        episodeTitle,
        episodeDescription,
        episodeDuration: Number(episodeDuration),
        episodeImage: episodeImageUrl,
        episodeScenes: uploadedScenes,
        episodeSeason: selectedSeason,
        episodeDate: episodeDate,
        lastUpdated: serverTimestamp()
      };
      
      await updateDoc(episodeRef, updatedEpisodeData);
      
      // Call the calculateStreak function
      try {
        const functions = getFunctions();
        const calculateStreak = httpsCallable(functions, 'calculateStreak');
        
        await calculateStreak({
          showId,
          latestEpisodeDate: episodeDate.getTime()
        });
        
        console.log('Successfully calculated streak');
      } catch (error) {
        console.error('Error calculating streak:', error);
      }
      
      // Close the modal
      onClose(true);
      
    } catch (err) {
      console.error('Error updating episode:', err);
      setError('Failed to update episode. Please try again.');
    } finally {
      setSavingEpisode(false);
    }
  };
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      backdropFilter: 'blur(5px)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        borderRadius: '0.75rem',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1rem 1.5rem',
          borderBottom: '1px solid #333'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '1.25rem',
            fontWeight: 'bold',
            color: 'white'
          }}>
            Edit Episode
          </h2>
          
          <button
            onClick={() => onClose()}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#999',
              cursor: 'pointer',
              display: 'flex',
              padding: '0.5rem'
            }}
          >
            <X size={24} />
          </button>
        </div>
        
        {/* Content - Scrollable */}
        <div style={{
          padding: '1.5rem',
          overflowY: 'auto',
          maxHeight: 'calc(90vh - 70px)',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem'
        }}>
          {/* Show name display */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              color: '#999'
            }}>
              Show
            </label>
            <div style={{
              fontSize: '1.1rem',
              fontWeight: 'bold',
              color: 'white'
            }}>
              {showName}
            </div>
          </div>
          
          {/* Episode title */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: 'bold',
              color: 'white'
            }}>
              Episode Title
            </label>
            <input
              type="text"
              value={episodeTitle}
              onChange={(e) => setEpisodeTitle(e.target.value)}
              placeholder="Enter episode title"
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: titleError ? '1px solid #E50914' : '1px solid transparent',
                borderRadius: '0.5rem',
                color: 'white',
                fontSize: '1rem'
              }}
            />
            {titleError && (
              <p style={{
                margin: '0.5rem 0 0 0',
                fontSize: '0.8rem',
                color: '#E50914'
              }}>
                Title must be 26 characters or less
              </p>
            )}
          </div>
          
          {/* Episode description */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: 'bold',
              color: 'white'
            }}>
              Episode Description
            </label>
            <textarea
              value={episodeDescription}
              onChange={(e) => setEpisodeDescription(e.target.value)}
              placeholder="Enter episode description"
              rows={4}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: descriptionError ? '1px solid #E50914' : '1px solid transparent',
                borderRadius: '0.5rem',
                color: 'white',
                fontSize: '1rem',
                resize: 'vertical'
              }}
            />
            {descriptionError && (
              <p style={{
                margin: '0.5rem 0 0 0',
                fontSize: '0.8rem',
                color: '#E50914'
              }}>
                Description must be 26 words or less
              </p>
            )}
          </div>
          
          {/* Season and Duration row */}
          <div style={{
            display: 'flex',
            gap: '1rem'
          }}>
            {/* Season selector */}
            <div style={{ flex: 1 }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.9rem',
                fontWeight: 'bold',
                color: 'white'
              }}>
                Season
              </label>
              <div style={{ position: 'relative' }}>
                <div
                  onClick={() => setShowSeasonSelector(!showSeasonSelector)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.75rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    color: 'white'
                  }}
                >
                  Season {selectedSeason}
                  <ChevronDown size={16} />
                </div>
                
                {showSeasonSelector && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '0.25rem',
                    backgroundColor: '#333',
                    borderRadius: '0.5rem',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                    zIndex: 10
                  }}>
                    {Array.from({ length: episode.showCurrentSeason || 1 }, (_, i) => (
                      <div
                        key={i}
                        onClick={() => handleSeasonChange(i + 1)}
                        style={{
                          padding: '0.75rem',
                          cursor: 'pointer',
                          backgroundColor: selectedSeason === i + 1 ? '#6600CC' : 'transparent',
                          color: 'white',
                          borderRadius: i === 0 ? '0.5rem 0.5rem 0 0' : i === (episode.showCurrentSeason || 1) - 1 ? '0 0 0.5rem 0.5rem' : 'none'
                        }}
                      >
                        Season {i + 1}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Duration toggle */}
            <div style={{ flex: 1 }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.9rem',
                fontWeight: 'bold',
                color: 'white'
              }}>
                Duration
              </label>
              <div
                onClick={toggleDuration}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  color: 'white'
                }}
              >
                {episodeDuration} minutes
              </div>
            </div>
          </div>
          
          {/* Episode date */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: 'bold',
              color: 'white'
            }}>
              Episode Date
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                borderRadius: '0.5rem',
                color: 'white',
                fontSize: '1rem'
              }}
            />
          </div>
          
          {/* Episode cover image */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: 'bold',
              color: 'white'
            }}>
              Episode Cover Image
            </label>
            
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageChange}
              accept="image/*"
              style={{ display: 'none' }}
            />
            
            <div
              onClick={handleImageClick}
              style={{
                width: '100%',
                height: '200px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '0.5rem',
                overflow: 'hidden',
                cursor: 'pointer',
                position: 'relative'
              }}
            >
              {episodeImagePreview ? (
                <img
                  src={episodeImagePreview}
                  alt="Episode cover"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <div style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#999'
                }}>
                  <Image size={48} />
                  <p style={{
                    margin: '1rem 0 0 0',
                    fontSize: '0.9rem'
                  }}>
                    Click to upload cover image
                  </p>
                </div>
              )}
              
              {episodeImagePreview && (
                <div style={{
                  position: 'absolute',
                  bottom: '1rem',
                  right: '1rem',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  borderRadius: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Upload size={16} color="white" />
                  <span style={{
                    fontSize: '0.9rem',
                    color: 'white'
                  }}>
                    Change
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {/* Episode scenes */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: 'bold',
              color: 'white'
            }}>
              Episode Scenes (Optional)
            </label>
            
            <input
              type="file"
              ref={scenesInputRef}
              onChange={handleScenesChange}
              accept="image/*"
              multiple
              style={{ display: 'none' }}
            />
            
            {/* Scene previews */}
            {episodeScenesPreview.length > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                gap: '0.5rem',
                marginBottom: '0.75rem'
              }}>
                {episodeScenesPreview.map((preview, index) => (
                  <div 
                    key={index}
                    style={{
                      position: 'relative',
                      height: '100px',
                      borderRadius: '0.5rem',
                      overflow: 'hidden'
                    }}
                  >
                    <img 
                      src={preview} 
                      alt={`Scene ${index + 1}`} 
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    
                    <button
                      onClick={() => handleRemoveScene(index)}
                      style={{
                        position: 'absolute',
                        top: '0.5rem',
                        right: '0.5rem',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '2rem',
                        height: '2rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <X size={16} color="white" />
                    </button>
                    
                    <div style={{
                      position: 'absolute',
                      bottom: '0.5rem',
                      left: '0.5rem',
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      borderRadius: '0.25rem',
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.8rem',
                      color: 'white'
                    }}>
                      {index + 1}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add scenes button */}
            <button
              onClick={() => scenesInputRef.current.click()}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                borderRadius: '0.5rem',
                color: 'white',
                fontSize: '0.9rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <Camera size={18} />
              {episodeScenesPreview.length > 0 ? 'Add More Scenes' : 'Add Episode Scenes'}
            </button>
          </div>
          
          {/* Error message */}
          {error && (
            <div style={{
              padding: '0.75rem',
              backgroundColor: 'rgba(229, 9, 20, 0.1)',
              borderRadius: '0.5rem',
              color: '#E50914',
              fontSize: '0.9rem'
            }}>
              {error}
            </div>
          )}
          
          {/* Submit button */}
          <button
            onClick={handleSubmit}
            disabled={savingEpisode || !episodeTitle.trim() || !episodeDescription.trim() || !episodeImagePreview || titleError || descriptionError}
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: (savingEpisode || !episodeTitle.trim() || !episodeDescription.trim() || !episodeImagePreview || titleError || descriptionError) 
                ? 'rgba(200, 200, 200, 0.3)' 
                : '#6600CC',
              color: (savingEpisode || !episodeTitle.trim() || !episodeDescription.trim() || !episodeImagePreview || titleError || descriptionError) 
                ? '#999' 
                : 'white',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: (savingEpisode || !episodeTitle.trim() || !episodeDescription.trim() || !episodeImagePreview || titleError || descriptionError) 
                ? 'not-allowed' 
                : 'pointer',
              opacity: savingEpisode ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            {savingEpisode ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditEpisodeModal; 